import React, { useMemo } from 'react';
import { Card } from 'react-bootstrap';
import { ChevronLeft, ChevronRight } from 'react-bootstrap-icons';
import { useTheme } from '../../contexts/ThemeContext';
import useIsMobile from '../../hooks/useIsMobile';

const DAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const LEGEND_ITEMS = [
  { color: '#22C55E', label: 'Complete' },
  { color: '#F59E0B', label: 'Partial' },
  { color: '#EF4444', label: 'None' },
];

function getDayColor(tradeCount, journaledCount) {
  if (tradeCount === 0) return { bg: 'transparent', text: null };
  if (journaledCount === 0) return { bg: '#ef444433', text: '#ef4444' };        // red — same as max daily loss
  if (journaledCount < tradeCount) return { bg: '#F59E0B33', text: '#F59E0B' }; // orange — partial
  return { bg: '#22c55e33', text: '#22C55E' };                                  // green — complete
}

const JournalCalendar = ({ days = [], month, onMonthChange, onDayClick, bare = false, legendBelow, fillHeight = true }) => {
  const { colors } = useTheme();
  const isMobile = useIsMobile();
  const showLegendBelow = legendBelow ?? isMobile;

  const { year, mon, monthLabel, calendarDays } = useMemo(() => {
    const [y, m] = (month || '').split('-').map(Number);
    if (!y || !m) return { year: 0, mon: 0, monthLabel: '', calendarDays: [] };

    const date = new Date(y, m - 1, 1);
    const monthLabel = date.toLocaleString('default', { month: 'short', year: 'numeric' });
    const firstDow = date.getDay();
    const daysInMonth = new Date(y, m, 0).getDate();

    const dayMap = {};
    for (const d of days) {
      dayMap[d.date] = d;
    }

    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      cells.push({ dateStr, d, ...(dayMap[dateStr] || { tradeCount: 0, journaledCount: 0 }) });
    }

    return { year: y, mon: m, monthLabel, calendarDays: cells };
  }, [month, days]);

  const handlePrev = () => {
    const d = new Date(year, mon - 2, 1);
    onMonthChange(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const handleNext = () => {
    const d = new Date(year, mon, 1);
    onMonthChange(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  // Local calendar date — toISOString() is UTC and flips to "tomorrow" after ~5pm PDT.
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  const legend = (
    <div style={{ display: 'flex', gap: 8, flexShrink: 1, minWidth: 0, justifyContent: showLegendBelow ? 'flex-end' : 'flex-start', flexWrap: 'wrap' }}>
      {LEGEND_ITEMS.map(({ color, label }) => (
        <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
          <span style={{ fontSize: '0.65rem', color: colors.textMuted, whiteSpace: 'nowrap' }}>{label}</span>
        </div>
      ))}
    </div>
  );

  const monthNav = (
    <div style={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', flexShrink: 0 }}>
      <button onClick={handlePrev} style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', padding: '2px 4px', lineHeight: 1 }}>
        <ChevronLeft size={13} />
      </button>
      <span style={{ fontSize: '0.7rem', fontWeight: 600, color: colors.textMuted, minWidth: 64, textAlign: 'center' }}>{monthLabel}</span>
      <button onClick={handleNext} style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', padding: '2px 4px', lineHeight: 1 }}>
        <ChevronRight size={13} />
      </button>
    </div>
  );

  const inner = (
    <div style={{ padding: bare ? 0 : 12, display: 'flex', flexDirection: 'column', flex: fillHeight ? 1 : 'none', height: fillHeight ? '100%' : 'auto', minWidth: 0, position: 'relative', zIndex: 1 }}>

        {/* Header row — title | legend (desktop) | month nav */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, minWidth: 0 }}>
          <div style={{ fontSize: '0.72rem', color: colors.text, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, flexShrink: 0, whiteSpace: 'nowrap' }}>
            Journaling
          </div>
          {!showLegendBelow && legend}
          {monthNav}
        </div>

        {/* Day-of-week labels */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 2 }}>
          {DAY_LABELS.map((l, i) => (
            <div key={i} style={{ textAlign: 'center', fontSize: '0.55rem', color: colors.textMuted, fontWeight: 500 }}>{l}</div>
          ))}
        </div>

        {/* Calendar grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
          {calendarDays.map((cell, i) => {
            if (!cell) return <div key={`e-${i}`} style={{ height: 28 }} />;
            const { bg, text } = getDayColor(cell.tradeCount, cell.journaledCount);
            const isToday = cell.dateStr === today;
            return (
              <div
                key={cell.dateStr}
                onClick={() => cell.tradeCount > 0 && onDayClick?.(cell.dateStr)}
                style={{
                  height: 35,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 3,
                  backgroundColor: bg,
                  color: text || colors.textMuted,
                  fontSize: '0.6rem',
                  fontWeight: isToday ? 700 : 400,
                  cursor: cell.tradeCount > 0 ? 'pointer' : 'default',
                  outline: isToday ? '1px solid #FFFFFF' : 'none',
                  outlineOffset: 1,
                  transition: 'opacity 0.12s',
                }}
                title={cell.tradeCount > 0 ? `${cell.journaledCount}/${cell.tradeCount} journaled` : undefined}
                onMouseEnter={e => { if (cell.tradeCount > 0) e.currentTarget.style.opacity = '0.65'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
              >
                {cell.d}
              </div>
            );
          })}
        </div>

        {showLegendBelow && (
          <div style={{ marginTop: fillHeight ? 'auto' : 8, paddingTop: 8, display: 'flex', justifyContent: 'flex-end', position: 'relative', zIndex: 2 }}>
            {legend}
          </div>
        )}

      </div>
  );

  if (bare) return inner;

  return (
    <Card style={{
      backgroundColor: colors.bgCard,
      border: `1px solid ${colors.border}`,
      borderRadius: '8px',
      height: fillHeight ? '100%' : 'auto',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      zIndex: 1,
      overflow: 'hidden',
      flexShrink: 0,
    }}>
      <Card.Body style={{ padding: '12px', display: 'flex', flexDirection: 'column', flex: fillHeight ? 1 : 'none' }}>
        {inner}
      </Card.Body>
    </Card>
  );
};

export default JournalCalendar;
